// src/live/utils/firestoreWriteWithFallback.ts
//
// Shared utility that wraps the "try standard Firestore SDK → on WebChannel
// transport error → retry with Firestore Lite SDK" pattern used across the
// live tab services.

/**
 * Returns `true` when the error looks like a Firestore WebChannel / gRPC
 * transport failure — the class of transient errors where the standard SDK
 * write fails but the Lite (REST-based) SDK can still succeed.
 */
export const isWebChannelTransportError = (err: unknown): boolean => {
  const message = String((err as { message?: unknown })?.message ?? '').toLowerCase();
  return (
    message.includes('webchannel') ||
    message.includes('transport errored') ||
    message.includes('write/channel') ||
    message.includes('google.firestore.v1.firestore/write')
  );
};

export interface WriteWithFallbackOptions {
  /** Maximum number of fallback retry attempts (default: 2). */
  retries?: number;
  /** Label used in console.error messages for easier debugging. */
  label?: string;
}

/**
 * Attempts `primaryWrite` first. If it fails with a WebChannel transport
 * error the function retries using `fallbackWrite` (up to `retries` times
 * with 1 s exponential back-off between fallback attempts).
 *
 * Non-WebChannel errors from `primaryWrite` are re-thrown immediately.
 * If all fallback attempts are also exhausted the last fallback error is
 * thrown.
 */
export async function firestoreWriteWithFallback<T>(
  primaryWrite: () => Promise<T>,
  fallbackWrite: () => Promise<T>,
  options?: WriteWithFallbackOptions,
): Promise<T> {
  const { retries = 2, label = 'firestoreWriteWithFallback' } = options ?? {};

  // --- primary attempt ---
  try {
    return await primaryWrite();
  } catch (primaryErr) {
    if (!isWebChannelTransportError(primaryErr)) {
      throw primaryErr;
    }

    // --- fallback attempts ---
    let lastErr: unknown = primaryErr;
    for (let attempt = 0; attempt < retries; attempt += 1) {
      try {
        return await fallbackWrite();
      } catch (fallbackErr) {
        lastErr = fallbackErr;
        console.error(
          `[${label}] Fallback attempt ${attempt + 1}/${retries} failed:`,
          fallbackErr,
        );
        if (attempt < retries - 1) {
          // exponential back-off: 1s, 2s, 4s …
          await new Promise((r) => setTimeout(r, 1_000 * Math.pow(2, attempt)));
        }
      }
    }

    throw lastErr;
  }
}
